<template>
	<div>
        <div class="modal-header flex items-center justify-between">
            <h1 class="flex-1 text-2xl">Manage Project Sites</h1>
            <div @click="hideModal">
                <inline-svg name="close" classes="w-5 h-5 text-white fill-current cursor-pointer"></inline-svg>
            </div>
        </div>
        <div class="pb-4 modal-toolbar flex items-center justify-between">
            <div class="flex">
                <h1 class="module-header text-2xl">Sites</h1>
                <div class="ml-3 flex items-center text-gray-600">
                    <div class="text-2xl">/</div>
                    <div class="ml-2 pt-1 text-xl">{{ filteredSites.length }} of {{ sites.all.length }}</div>
                </div>
            </div>
            <div v-if="showFilters" class="flex items-center" :class="{'flex-1 justify-end pt-4 pb-3': ! showHeader}">
                <filters
                    data-type="sites"
                    sort-by="building.address_line_1"
                    :filters="filters"
                    portal="no-sites-found"
                    :show-view-options="false"
                    v-on:view-change="view = $event">

                    <template slot="filters">
                        <div>
                            <span class="mr-6">Show Archived Sites</span>
                            <toggle-button v-model="showArchivedSites" color="#38C172" sync></toggle-button>
                        </div>
                    </template>
                </filters>
            </div>
        </div>

        <div class="py-4 px-10">
            <paginate name="sites" :list="filteredSites" :per="perPage" class="paginate-list">
                <div class="mt-5 grid grid-cols-3 gap-x-6 gap-y-12" :class="{ 'mx-auto': centerCards }">
                    <div class="card relative" v-for="site in paginated('sites')" :key="site.id" :class="{'hidden': ! showArchivedSites && site.archived_at !== null }">
                        <div class="w-full h-full flex border rounded-lg shadow-lg">
                            <div class="w-full flex flex-col">
                                <div class="flex flex-1">
                                    <div class="absolute top-0 right-0 badge border rounded rounded-br-none" :class="site.status_color">
                                        <div class="flex items-baseline font-variant-all-small-caps space-x-1">
                                            <div v-if="site.lease_or_own == 'Owned'">Owned</div>
                                            <div v-else-if="site.leases.length && site.due_in_days > 0">Due in <span class="font-bold">{{ site.due_in_text }}</span></div>
                                            <div v-else-if="site.leases.length && site.due_in_days == 0">Expired</div>
                                            <div v-else>No Lease Information</div>
                                        </div>
                                    </div>
                                    <div
                                        class="flex w-full"
                                        :class="{ 'bg-red-400 text-white': site.is_upcoming }"
                                    >
                                        <div class="-mt-2 -ml-2 absolute top-0 left-0 z-40">
                                            <div class="flex justify-center items-center w-10 h-10 shadow-md text-xs text-white bg-white border border-gray-100 hover:cursor-pointer">
                                                <input @click="toggleProjectSite(site)" type="checkbox" class="form-checkbox text-green w-8 h-8 rounded-full hover:cursor-pointer" :checked="isInProject(site.id)">
                                            </div>
                                        </div>
                                        <div
                                            class="w-40 h-full relative"
                                            :class="site.is_upcoming ? 'text-red-400' : 'text-white'"
                                        >
                                            <div v-if="site.archived_at !== null" class="mt-4 ml-4 absolute top-0 left-0 z-40" title="Site is Archived">
                                                <inline-svg name="archived"></inline-svg>
                                            </div>
                                            <img class="w-full h-full max-h-[158px] object-cover border-r border-white rounded-tl-lg" :src="site.building.image.view_url">
                                            <inline-svg name="image-skewer-right"></inline-svg>
                                        </div>
                                        <div class="mx-4 mt-12 mb-10 flex flex-1 flex-col justify-center">
                                            <div class="uppercase">
                                                <p class="text-lg font-semibold" :title="site.building.address_line_1">{{ site.building.address_line_1 | truncate(25) }}</p>
                                                <p class="text-sm">{{ site.building.city }}, {{ site.building.state }}</p>
                                                <p class="text-sm">{{ site.building.postal_code }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="py-4 px-5 flex justify-between bg-gray-200">
                                    <div>
                                        <div v-if="site.utilization" class="badge" :class="site.utilization_color">
                                            <div class="flex items-baseline space-x-1">
                                                <span class="font-extrabold">{{ site.utilization }}%</span>
                                                <span class="text-base font-variant-all-small-caps">Utilized</span>
                                            </div>
                                        </div>

                                        <div v-else></div>
                                    </div>

                                    <div class="badge badge-red">
                                        <div class="flex items-baseline space-x-1">
                                            <span :data-originalvalue="site.shortened_remaining_lease_cost" class="is-shorthand-currency font-extrabold">${{ site.shortened_remaining_lease_cost }}</span>
                                            <span class="text-base font-variant-all-small-caps">Remaining</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </paginate>
            <paginate-links for="sites" :show-step-links="true" :hide-single-page="true"></paginate-links>
        </div>

        <portal-target name="no-sites-found"></portal-target>
	</div>
</template>

<script>
	import { mapState } from 'vuex';

	export default {
		props: {
			projectId: {
				type: Number,
				required: true
			},

			siteData: {
				type: [Array, Object],
				required: true
			},

            showHeader: {
              type: Boolean,
              default: true
            },

            smallHeader: {
              type: Boolean,
              default: false
            },

            showFilters: {
              type: Boolean,
              default: true
            },

            centerCards: {
              type: Boolean,
              default: false
            },

            perPage: {
                type: Number,
                default: 10
            }
		},

		data() {
			return {
                paginate: ['sites'],
                showArchivedSites: false,
				involvedSites: this.siteData,
			}
		},

        computed: {
            ...mapState(['sites']),
            ...mapState(['projectSites']),

            filteredSites() {
            	const sites = _.filter(this.sites.filtered, (site) => {
  					return ! this.isInProject(site.id);
				});

            	return _.concat(this.involvedSites, sites);
            },

            filters() {
                return {
                    showArchivedSites: this.showArchivedSites
                }
            }
        },

        methods: {
        	isInProject(siteId) {
        		return _.includes(_.map(this.involvedSites, 'id'), siteId);
        	},

        	toggleProjectSite(site) {
        		if (this.isInProject(site.id)) {
        			this.removeSite(site.pivot.id);
        		} else {
        			this.addSite(site.id);
        		}
        	},

        	addSite(siteId) {
                axios.post("/projects/" + this.projectId + "/sites", {
                	'site_id': siteId
                }).then((response) => {
					this.updateSites(response.data.sites);
                	
					this.$notify({
						type: 'success',
						title: 'Success',
						text: response.data.message
					});
				}).catch((error) => {
                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response.data.message
                    });
                });
        	},

        	removeSite(siteId) {
                axios.delete("/projects/" + this.projectId + "/sites/" + siteId).then((response) => {
                	this.updateSites(response.data.sites);

					this.$notify({
						type: 'success',
						title: 'Success',
						text: response.data.message
					});                    
                }).catch((error) => {
                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response.data.message
                    });
                });
			},

			updateSites(sites) {
            	this.involvedSites = sites;
            	this.projectSites.all = sites;
            	this.projectSites.filtered = sites;

            	this.$emit('sites-updated', sites);
			}
        }
	}
</script>